import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import auth from '../../utils/auth';

const ResetPassword = () => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [userSession, setUserSession] = useState(null);
  const [requiresMFA, setRequiresMFA] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const history = useHistory();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const response = await auth.confirmPasswordReset(email, code, newPassword);

      
      
      // Check if MFA is required
      if (response.requiresMFA && response.challengeName === 'CUSTOM_CHALLENGE') {
        setUserSession(response.session);
        setRequiresMFA(true);
        setIsLoading(false);
        return;
      }
      
      if (response.success) {
        history.push('/login', { 
          notification: 'Password has been reset successfully. You can now log in with your new password.'
        });
      } else {
        setError(response.message || 'Failed to reset password');
      }
    } catch (err) {
      console.error('Password reset error:', err);
      setError(
        err.error || 
        err.response?.data?.error || 
        err.message || 
        'Failed to reset password'
      );
    }
    
    setIsLoading(false);
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const response = await auth.verifyOTP(email, userSession, otpCode);
      
      if (response.success) {
        history.push('/dashboard');
      } else {
        setError(response.error || 'Failed to verify OTP code');
      }
    } catch (err) {
      console.error('OTP verification error:', err);
      setError(
        err.error || 
        err.response?.data?.error || 
        err.message || 
        'Failed to verify OTP code'
      );
    }
    
    setIsLoading(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Show OTP form if MFA is required
  if (requiresMFA) {
    return (
      <form className="mb-6" onSubmit={handleOTPSubmit}>
        <div className="mb-6">
          <h4>Email Verification Code</h4>
          <p>Please enter the verification code sent to your email.</p>
        </div>
        
        {error && <div className="alert alert-danger mb-4">{error}</div>}
        
        <div className="mb-6 form-control-validation">
          <label className="form-label" htmlFor="otpCode">Verification Code</label>
          <input
            type="text"
            id="otpCode"
            className="form-control"
            placeholder="Enter verification code"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value)}
            required
            autoFocus
          />
        </div>
        
        <button 
          className="btn btn-primary d-grid w-100" 
          type="submit"
          disabled={isLoading}
        >
          {isLoading ? 'Verifying...' : 'Verify Code'}
        </button>
      </form>
    );
  }

  return (
    <>
      <h4 className="mb-1">Reset Password 🔒</h4>
      <p className="mb-6">Enter your email, the reset code we sent, and your new password</p>
      
      <form className="mb-6" onSubmit={handleSubmit}>
        {error && <div className="alert alert-danger mb-4">{error}</div>}
        
        <div className="mb-6 form-control-validation">
          <label htmlFor="email" className="form-label">Email</label>
          <input
            type="email"
            className="form-control"
            id="email"
            name="email"
            placeholder="Enter your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        
        <div className="mb-6 form-control-validation">
          <label htmlFor="code" className="form-label">Reset Code</label>
          <input
            type="text"
            className="form-control"
            id="code"
            name="code"
            placeholder="Enter the reset code from your email"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
          />
        </div>
        
        <div className="mb-6 form-password-toggle form-control-validation">
          <label className="form-label" htmlFor="newPassword">New Password</label>
          <div className="input-group input-group-merge">
            <input
              type={showPassword ? "text" : "password"}
              id="newPassword"
              name="newPassword"
              className="form-control"
              placeholder="············"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
            <span className="input-group-text cursor-pointer" onClick={togglePasswordVisibility}>
              <i className={`icon-base ti ${showPassword ? "tabler-eye" : "tabler-eye-off"}`}></i>
            </span>
          </div>
        </div>
        
        <div className="mb-6 form-password-toggle form-control-validation">
          <label className="form-label" htmlFor="confirmPassword">Confirm Password</label>
          <div className="input-group input-group-merge">
            <input
              type={showPassword ? "text" : "password"}
              id="confirmPassword"
              name="confirmPassword"
              className="form-control"
              placeholder="············"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
            <span className="input-group-text cursor-pointer" onClick={togglePasswordVisibility}>
              <i className={`icon-base ti ${showPassword ? "tabler-eye" : "tabler-eye-off"}`}></i>
            </span>
          </div>
        </div>
        
        <button 
          className="btn btn-primary d-grid w-100" 
          type="submit"
          disabled={isLoading}
        >
          {isLoading ? 'Resetting...' : 'Reset Password'}
        </button>
      </form>

      <p className="text-center">
        <Link to="/login">
          <i className="icon-base ti tabler-arrow-left"></i> Back to login
        </Link>
      </p>
    </>
  );
};

export default ResetPassword;